/* eslint-disable react-refresh/only-export-components */
// ════════════════════════════════════════════════════════════
// ConfirmDialog — alertdialog opinionated (Radix AlertDialog)
// ════════════════════════════════════════════════════════════
// 3 usages :
//   1. <ConfirmDialog trigger={...}> — gère l'état localement
//   2. <ConfirmDialog open onOpenChange> — controlled
//   3. confirmAsync({ ... }) — helper impératif (requiert <ConfirmProvider>)
//
// Variants : 'default' (info, bouton primary) | 'destructive' (rouge).
// Le bouton confirm passe automatiquement en loading si `onConfirm`
// retourne une Promise (et reste dispo via la prop `isLoading` pour
// une mutation extérieure).
// ════════════════════════════════════════════════════════════
import * as React from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { useModalStack, backdropOpacityClass } from './hooks/use-modal-stack';
import { useConfirmStore, type ConfirmOptions } from './hooks/use-confirm';

export type ConfirmVariant = 'default' | 'destructive';

export interface ConfirmDialogProps {
  /** Trigger React (asChild) — alternative à la version controlled. */
  trigger?: React.ReactNode;
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  title: string;
  description?: string | React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmVariant;
  onConfirm: () => void | Promise<void>;
  onCancel?: () => void;
  /** Force l'état loading (utile si mutation TanStack Query extérieure). */
  isLoading?: boolean;
  /** Désactive la fermeture à l'Escape. Défaut true (Escape ferme). */
  dismissibleEscape?: boolean;
}

function ConfirmDialogInner({
  trigger,
  open,
  defaultOpen,
  onOpenChange,
  title,
  description,
  confirmLabel = 'Confirmer',
  cancelLabel = 'Annuler',
  variant = 'default',
  onConfirm,
  onCancel,
  isLoading,
  dismissibleEscape = true,
}: ConfirmDialogProps) {
  const [internalLoading, setInternalLoading] = React.useState(false);
  const loading = isLoading ?? internalLoading;
  const isControlled = open !== undefined;
  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(defaultOpen ?? false);
  const actualOpen = isControlled ? open : uncontrolledOpen;

  const stack = useModalStack(Boolean(actualOpen));

  const handleOpenChange = (next: boolean) => {
    if (loading && !next) return; // bloque la fermeture pendant loading
    if (next) {
      // Évite le warning Chrome « aria-hidden on focused descendant » :
      // on retire le focus du trigger avant que Radix ne marque ses
      // siblings aria-hidden (cf. blur dans use-prevent-close).
      if (
        typeof document !== 'undefined' &&
        document.activeElement instanceof HTMLElement &&
        document.activeElement !== document.body
      ) {
        document.activeElement.blur();
      }
    }
    if (!isControlled) setUncontrolledOpen(next);
    onOpenChange?.(next);
  };

  const handleConfirm = async (e: Event) => {
    e.preventDefault(); // empêche la fermeture auto Radix tant que onConfirm n'est pas résolu
    setInternalLoading(true);
    try {
      await onConfirm();
      handleOpenChange(false);
    } finally {
      setInternalLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel?.();
  };

  // L'overlay opaque est rendu UNIQUEMENT par le modal le plus bas.
  const showOpaqueBackdrop = stack.index === 0;

  return (
    <AlertDialog.Root open={actualOpen} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      {trigger ? <AlertDialog.Trigger asChild>{trigger}</AlertDialog.Trigger> : null}
      <AlertDialog.Portal>
        <AlertDialog.Overlay
          className={cn(
            'fixed inset-0 z-50',
            showOpaqueBackdrop ? backdropOpacityClass(stack.depth) : 'bg-transparent',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'motion-reduce:animate-none',
          )}
        />
        <AlertDialog.Content
          asChild
          onEscapeKeyDown={(e) => {
            if (!dismissibleEscape || loading) e.preventDefault();
          }}
        >
          <motion.div
            className={cn(
              // max-w-[calc(100vw-2rem)] garantit 16 px de gouttière de
              // chaque côté à partir d'iPhone SE (320 px). Au-dessus de
              // sm: on plafonne à 448 px (taille de référence).
              'bg-card text-card-foreground fixed left-1/2 top-1/2 z-50 grid w-full max-w-[calc(100vw-2rem)] -translate-x-1/2 -translate-y-1/2 gap-0 rounded-lg shadow-2xl sm:max-w-md',
              'data-[state=open]:animate-in data-[state=closed]:animate-out',
              'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
              'data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2',
              'duration-200 motion-reduce:animate-none motion-reduce:duration-0',
            )}
          >
            <div className="space-y-2 px-4 py-5 sm:px-6">
              <AlertDialog.Title className="text-base font-semibold leading-tight tracking-tight">
                {title}
              </AlertDialog.Title>
              {description ? (
                <AlertDialog.Description className="text-muted-foreground text-sm">
                  {description}
                </AlertDialog.Description>
              ) : (
                // Pas de description visible → fallback a11y caché (le title sert
                // de description aux lecteurs d'écran, Radix arrête de râler).
                <VisuallyHidden.Root>
                  <AlertDialog.Description>{title}</AlertDialog.Description>
                </VisuallyHidden.Root>
              )}
            </div>
            <div className="border-border flex flex-col-reverse gap-2 border-t px-4 py-4 min-[420px]:flex-row min-[420px]:justify-end sm:px-6">
              <AlertDialog.Cancel asChild>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={loading}
                  className="w-full min-[420px]:w-auto"
                >
                  {cancelLabel}
                </Button>
              </AlertDialog.Cancel>
              <AlertDialog.Action
                asChild
                onClick={(e) => {
                  // Radix gère onClick mais on a besoin de preventDefault async-friendly
                  void handleConfirm(e.nativeEvent);
                }}
              >
                <Button
                  variant={variant === 'destructive' ? 'destructive' : 'default'}
                  loading={loading}
                  data-testid="confirm-dialog-action"
                  className="w-full min-[420px]:w-auto"
                >
                  {confirmLabel}
                </Button>
              </AlertDialog.Action>
            </div>
          </motion.div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}

/**
 * ConfirmDialog — confirmation opinionated avec variant default / destructive.
 *
 * @example
 * <ConfirmDialog
 *   trigger={<Button variant="destructive">Supprimer</Button>}
 *   title="Supprimer ce voyage ?"
 *   description="Cette action est irréversible."
 *   confirmLabel="Supprimer"
 *   variant="destructive"
 *   onConfirm={async () => { await deleteVoyage(id); }}
 * />
 */
function ConfirmDialog(props: ConfirmDialogProps) {
  return <ConfirmDialogInner {...props} />;
}
ConfirmDialog.displayName = 'ConfirmDialog';

// ════════════════════════════════════════════════════════════
// ConfirmProvider — rend UN ConfirmDialog global lié au store
// ════════════════════════════════════════════════════════════
// À monter à la racine de l'app (apps/web/src/routes/__root.tsx).
// Permet d'utiliser `await confirmAsync({...})` de n'importe où.
// ════════════════════════════════════════════════════════════

interface ConfirmProviderProps {
  children: React.ReactNode;
}

function ConfirmProvider({ children }: ConfirmProviderProps) {
  const open = useConfirmStore((s) => s.open);
  const options = useConfirmStore((s) => s.options);
  const resolve = useConfirmStore((s) => s.resolve);

  const lastOptionsRef = React.useRef<ConfirmOptions | null>(options);
  if (options) lastOptionsRef.current = options;
  const displayedOptions = options ?? lastOptionsRef.current;

  return (
    <>
      {children}
      {displayedOptions ? (
        <ConfirmDialogInner
          open={open}
          onOpenChange={(next) => {
            if (!next) resolve(false);
          }}
          title={displayedOptions.title}
          description={displayedOptions.description}
          confirmLabel={displayedOptions.confirmLabel}
          cancelLabel={displayedOptions.cancelLabel}
          variant={displayedOptions.variant}
          onConfirm={() => {
            resolve(true);
          }}
        />
      ) : null}
    </>
  );
}
ConfirmProvider.displayName = 'ConfirmProvider';

export { ConfirmDialog, ConfirmProvider };
export { confirmAsync, useConfirmStore, type ConfirmOptions } from './hooks/use-confirm';
